import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDatasets } from "../context/DatasetContext";
import axios from "axios";
import { readCache, writeCache } from "../utils/localCache";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { ProgressPanel } from "../components/ProgressPanel";
import { getPartyColor, getPartyTextClass } from "../utils/partyUtils";
import {
  DollarSign,
  Users,
  Building2,
  Siren,
  TrendingUp,
  Calendar,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Hash,
  ChevronRight,
  Database,
  BarChart3,
  Award,
  MapPin,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  ComposedChart,
  LabelList,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Dashboard phase list — mirrors the six parallel backend aggregations
// (stats, by-year, by-year-party, top donors, top recipients, geocode
// status). Rotating through them gives users a sense of steady progress
// during the ~15 s cold load on huge folders.
const DASHBOARD_PHASES = [
  { icon: Database,   label: "Loading donation stats",              min: 0 },
  { icon: Calendar,   label: "Grouping donations by year",          min: 3 },
  { icon: BarChart3,  label: "Building party-affiliation chart",    min: 5 },
  { icon: Award,      label: "Ranking top donors",                  min: 7 },
  { icon: Building2,  label: "Ranking top recipients",              min: 9 },
  { icon: MapPin,     label: "Computing geocoding stats",           min: 11 },
];
const DASHBOARD_REASSURANCES = {
  5:  "This may take up to 15 seconds on first load — cached instantly afterwards.",
  10: "Aggregating a large dataset. Almost there…",
  20: "Very large folder — MongoDB is aggregating millions of donations. This result will be cached for next time.",
};

export default function DashboardHome() {
  const navigate = useNavigate();
  const { debouncedSelectedIds, selectedDatasets, displayTimeRange } = useDatasets();
  const [stats, setStats] = useState(null);
  const [yearData, setYearData] = useState([]);
  const [yearPartyData, setYearPartyData] = useState([]);
  const [topDonors, setTopDonors] = useState([]);
  const [topRecipients, setTopRecipients] = useState([]);
  const [geocodeStats, setGeocodeStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [donorSort, setDonorSort] = useState({ field: 'total', order: 'desc' });
  const [recipientSort, setRecipientSort] = useState({ field: 'total', order: 'desc' });
  const lastFetchedIds = useRef('');
  const fetchIdRef = useRef(0);

  useEffect(() => {
    // Always re-fetch if no datasets selected (to clear data)
    if (!debouncedSelectedIds) {
      fetchDashboardData();
      lastFetchedIds.current = '';
      return;
    }
    if (debouncedSelectedIds === lastFetchedIds.current && stats) {
      return;
    }
    // Dataset selection changed — only clear stale data if we don't have
    // a localStorage cache for the new selection (otherwise the cache
    // repaint inside fetchDashboardData handles the swap instantly).
    if (lastFetchedIds.current && debouncedSelectedIds !== lastFetchedIds.current) {
      const hasCachedNext = !!readCache('dashboard', debouncedSelectedIds);
      if (!hasCachedNext) {
        setStats(null);
        setYearData([]);
        setYearPartyData([]);
        setTopDonors([]);
        setTopRecipients([]);
        setGeocodeStats(null);
      }
    }
    fetchDashboardData();
  }, [debouncedSelectedIds]);

  // Auto-refresh dashboard when a committee is tagged/untagged so party-
  // coloured charts (top recipients, by-year-party) reflect the change.
  // Also wipe the localStorage cache: a tag mutation changes the dominant
  // party of every committee that contains the keyword, which affects
  // ALL cached dashboard combos.
  useEffect(() => {
    const onPartyTagChanged = () => {
      try { localStorage && Object.keys(localStorage).filter(k => k.startsWith('fec_lcache_v2:')).forEach(k => localStorage.removeItem(k)); } catch (_) {}
      fetchDashboardData();
    };
    window.addEventListener("party-tag-changed", onPartyTagChanged);
    return () => window.removeEventListener("party-tag-changed", onPartyTagChanged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retryCount = useRef(0);
  const MAX_RETRIES = 2;

  const fetchDashboardData = async () => {
    const thisId = ++fetchIdRef.current;

    // Instant first paint: if we have a localStorage snapshot for this
    // exact dataset combo, repaint immediately and let the network fetch
    // refresh silently in the background. Returning users see the
    // dashboard in <50ms even on a fresh deploy with cold backend caches.
    let paintedFromCache = false;
    if (debouncedSelectedIds) {
      const cached = readCache('dashboard', debouncedSelectedIds);
      if (cached && cached.stats) {
        if (cached.stats) setStats(cached.stats);
        setYearData(cached.year_data || []);
        setYearPartyData(cached.year_party_data || []);
        setTopDonors(cached.top_donors || []);
        setTopRecipients(cached.top_recipients || []);
        setGeocodeStats(cached.geocode_stats || null);
        lastFetchedIds.current = debouncedSelectedIds;
        paintedFromCache = true;
      }
    }

    // Treat the zero-placeholder `stats` (set when no dataset is selected) as
    // "no real data yet" so the first real fetch shows the ProgressPanel
    // instead of the silent background-refresh spinner.
    const hasRealData = stats && stats.total_donations > 0;
    const isInitial = !hasRealData && !paintedFromCache;
    if (isInitial) setLoading(true);
    else setRefreshing(true);

    if (!debouncedSelectedIds) {
      setStats({ total_donations: 0, total_amount: 0, unique_donors: 0, unique_recipients: 0, flagged_anomalies: 0, date_range: "N/A", avg_donation: 0 });
      setYearData([]); setYearPartyData([]); setTopDonors([]); setTopRecipients([]); setGeocodeStats(null);
      setLoading(false); setRefreshing(false);
      retryCount.current = 0;
      return;
    }

    const setData = (d) => {
      if (d.stats) setStats(d.stats);
      setYearData(d.year_data || []);
      setYearPartyData(d.year_party_data || []);
      setTopDonors(d.top_donors || []);
      setTopRecipients(d.top_recipients || []);
      setGeocodeStats(d.geocode_stats || null);
      lastFetchedIds.current = debouncedSelectedIds;
      retryCount.current = 0;
      // Persist for next visit's instant paint.
      try {
        writeCache('dashboard', debouncedSelectedIds, d);
      } catch (_) { /* ignore */ }
    };
    
    try {
      // Try combined cached endpoint first
      const res = await axios.get(`${API}/dashboard-data?dataset_ids=${debouncedSelectedIds}`, { timeout: 60000 });
      if (thisId !== fetchIdRef.current) return;
      setData(res.data);
    } catch (error) {
      console.error("Dashboard combined endpoint failed:", error?.response?.status, error?.message);
      if (thisId !== fetchIdRef.current) return;
      
      // Fallback: fetch from individual endpoints
      try {
        const p = `dataset_ids=${debouncedSelectedIds}`;
        const [statsRes, yearRes, yearPartyRes, donorsRes, recipientsRes, geocodeRes] = await Promise.all([
          axios.get(`${API}/stats?${p}`, { timeout: 30000 }).catch(e => { console.error("stats failed:", e?.message); return { data: null }; }),
          axios.get(`${API}/charts/by-year?${p}`, { timeout: 30000 }).catch(() => ({ data: [] })),
          axios.get(`${API}/charts/by-year-party?${p}`, { timeout: 30000 }).catch(() => ({ data: [] })),
          axios.get(`${API}/charts/top-donors?limit=20&${p}`, { timeout: 30000 }).catch(() => ({ data: [] })),
          axios.get(`${API}/charts/top-recipients?limit=20&${p}`, { timeout: 30000 }).catch(() => ({ data: [] })),
          axios.get(`${API}/geocoding-status?${p}`, { timeout: 15000 }).catch(() => ({ data: null })),
        ]);
        if (thisId !== fetchIdRef.current) return;
        setData({
          stats: statsRes.data,
          year_data: yearRes.data,
          year_party_data: yearPartyRes.data,
          top_donors: donorsRes.data,
          top_recipients: recipientsRes.data,
          geocode_stats: geocodeRes.data,
        });
      } catch (fallbackError) {
        console.error("Fallback also failed:", fallbackError);
        // Auto-retry up to MAX_RETRIES with backoff
        if (retryCount.current < MAX_RETRIES) {
          retryCount.current++;
          console.log(`Auto-retrying dashboard fetch (attempt ${retryCount.current}/${MAX_RETRIES})...`);
          setTimeout(() => {
            if (thisId === fetchIdRef.current) {
              fetchDashboardData();
            }
          }, retryCount.current * 3000);
          return; // Don't clear loading state yet
        }
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleDonorClick = (donorName) => {
    navigate(`/data?donor=${encodeURIComponent(donorName)}`);
  };

  const handleRecipientClick = (recipientName) => {
    navigate(`/data?recipient=${encodeURIComponent(recipientName)}`);
  };


  // Filter chart data by display time range
  const filteredYearData = yearData.filter(d => d.year >= displayTimeRange.start && d.year <= displayTimeRange.end);
  const filteredYearPartyData = yearPartyData.filter(d => d.year >= displayTimeRange.start && d.year <= displayTimeRange.end);

  const sortData = (data, sortConfig) => {
    return [...data].sort((a, b) => {
      const aVal = a[sortConfig.field];
      const bVal = b[sortConfig.field];
      if (sortConfig.order === 'asc') {
        return aVal - bVal;
      }
      return bVal - aVal;
    });
  };

  const handleDonorSort = (field) => {
    setDonorSort(prev => ({
      field,
      order: prev.field === field && prev.order === 'desc' ? 'asc' : 'desc'
    }));
  };

  const handleRecipientSort = (field) => {
    setRecipientSort(prev => ({
      field,
      order: prev.field === field && prev.order === 'desc' ? 'asc' : 'desc'
    }));
  };

  const sortedDonors = sortData(topDonors, donorSort);
  const sortedRecipients = sortData(topRecipients, recipientSort);

  const formatCurrency = (value) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
    return `$${value.toFixed(0)}`;
  };

  const formatNumber = (value) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value.toString();
  };

  if (loading) {
    const count = selectedDatasets ? selectedDatasets.length : 0;
    return (
      <ProgressPanel
        contextLabel={count > 0 ? `${count} dataset${count > 1 ? 's' : ''} selected` : ""}
        phases={DASHBOARD_PHASES}
        reassurances={DASHBOARD_REASSURANCES}
      />
    );
  }

  const statCards = [
    {
      title: "Total Donations",
      value: stats?.total_donations || 0,
      formatted: formatNumber(stats?.total_donations || 0),
      icon: TrendingUp,
      color: "text-primary",
    },
    {
      title: "Total Amount",
      value: stats?.total_amount || 0,
      formatted: formatCurrency(stats?.total_amount || 0),
      icon: DollarSign,
      color: "text-emerald-600",
    },
    {
      title: "Unique Donors",
      value: stats?.unique_donors || 0,
      formatted: formatNumber(stats?.unique_donors || 0),
      icon: Users,
      color: "text-blue-600",
      subtitle: geocodeStats && geocodeStats.total_addresses > 0
        ? `${Math.min(100, Math.round((geocodeStats.geocoded_count / geocodeStats.total_addresses) * 100))}% geocoded`
        : null,
    },
    {
      title: "Flagged Anomalies",
      value: stats?.flagged_anomalies || 0,
      formatted: stats?.flagged_anomalies?.toString() || "0",
      icon: Siren,
      color: "text-red-500",
    },
  ];

  return (
    <div className="p-4 md:p-8 space-y-6 md:space-y-8 relative" data-testid="dashboard-home">
      {/* Subtle refreshing overlay — keeps old data visible */}
      {refreshing && (
        <div className="absolute inset-0 bg-background/40 z-10 flex items-start justify-center pt-24" data-testid="dashboard-refreshing">
          <div className="flex items-center gap-2 bg-card px-4 py-2 rounded-sm shadow-sm border text-sm text-muted-foreground">
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            Updating data...
          </div>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Campaign finance data overview
            {stats?.date_range && stats.date_range !== "N/A" && (
              <span className="ml-2 inline-flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {stats.date_range}
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <Card 
            key={stat.title} 
            className="border rounded-sm shadow-sm animate-fade-in"
            style={{ animationDelay: `${index * 0.05}s` }}
            data-testid={`stat-card-${stat.title.toLowerCase().replace(' ', '-')}`}
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                    {stat.title}
                  </p>
                  <p className="font-mono text-2xl font-semibold mt-2 tracking-tight">
                    {stat.formatted}
                  </p>
                  {stat.subtitle && (
                    <p className="text-xs text-muted-foreground mt-1">{stat.subtitle}</p>
                  )}
                </div>
                <div className={`p-2 bg-muted rounded-sm ${stat.color}`}>
                  <stat.icon className="w-5 h-5" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Donations by Year - Stacked by Party */}
        <Card className="border rounded-sm shadow-sm" data-testid="chart-by-year">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg font-medium flex items-center justify-between">
              <span>Donations by Year</span>
              {filteredYearPartyData.length > 0 && (
                <div className="flex items-center gap-3 text-xs font-normal">
                  <span className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-blue-500" />
                    Democrat
                  </span>
                  <span className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-red-500" />
                    Republican
                  </span>
                  <span className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-slate-400" />
                    Other
                  </span>
                  <span className="flex items-center gap-1">
                    <div className="w-4 h-0.5 bg-green-600 rounded" />
                    # Donations
                  </span>
                </div>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredYearPartyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <ComposedChart data={filteredYearPartyData} margin={{ top: 20, right: 50, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis 
                    dataKey="year" 
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    axisLine={{ stroke: 'hsl(var(--border))' }}
                    tickLine={false}
                  />
                  <YAxis 
                    yAxisId="amount"
                    tickFormatter={formatCurrency}
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis 
                    yAxisId="count"
                    orientation="right"
                    tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v}
                    tick={{ fontSize: 11, fill: '#16a34a' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip 
                    formatter={(value, name) => {
                      if (name === 'count') return [value.toLocaleString(), '# Donations'];
                      const label = name === 'democrat' ? 'Democrat' : name === 'republican' ? 'Republican' : 'Other';
                      return [formatCurrency(value), label];
                    }}
                    contentStyle={{ 
                      background: 'hsl(var(--background))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '2px',
                      fontSize: '12px'
                    }}
                  />
                  <Bar yAxisId="amount" dataKey="democrat" stackId="a" fill="#2563eb" name="democrat" />
                  <Bar yAxisId="amount" dataKey="republican" stackId="a" fill="#dc2626" name="republican" />
                  <Bar yAxisId="amount" dataKey="other" stackId="a" fill="#94a3b8" name="other" radius={[2, 2, 0, 0]}>
                    <LabelList dataKey="total" position="top" formatter={formatCurrency} style={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} />
                  </Bar>
                  <Line 
                    yAxisId="count"
                    type="monotone" 
                    dataKey="count" 
                    stroke="#16a34a" 
                    strokeWidth={2} 
                    dot={{ r: 4, fill: '#16a34a' }}
                    name="count"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-muted-foreground">
                No data available. Upload a dataset to get started.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Donation Count Trend */}
        <Card className="border rounded-sm shadow-sm" data-testid="chart-count-trend">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg font-medium">Donation Count Trend</CardTitle>
          </CardHeader>
          <CardContent>
            {filteredYearData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={filteredYearData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis 
                    dataKey="year" 
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    axisLine={{ stroke: 'hsl(var(--border))' }}
                    tickLine={false}
                  />
                  <YAxis 
                    tickFormatter={formatNumber}
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip 
                    formatter={(value) => [formatNumber(value), "Donations"]}
                    contentStyle={{ 
                      background: 'hsl(var(--background))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '2px',
                      fontSize: '12px'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="count" 
                    stroke="hsl(var(--chart-5))"
                    strokeWidth={2}
                    dot={{ fill: 'hsl(var(--chart-5))', strokeWidth: 0, r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-muted-foreground">
                No data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top Donors & Recipients */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Donors */}
        <Card className="border rounded-sm shadow-sm" data-testid="top-donors-list">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg font-medium flex items-center gap-2 cursor-pointer hover:text-blue-600 transition-colors" onClick={() => navigate('/data')}>
              <Users className="w-4 h-4" />
              Top Donors
              <ChevronRight className="w-4 h-4 ml-auto" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topDonors.length > 0 ? (
              <div className="space-y-2">
                {/* Column Headers */}
                <div className="flex items-center py-2 border-b border-border text-xs uppercase tracking-wider text-muted-foreground font-medium pr-2">
                  <span className="w-6 shrink-0">#</span>
                  <span className="flex-1">Name</span>
                  <button 
                    onClick={(e) => { e.stopPropagation(); handleDonorSort('count'); }}
                    className="flex items-center gap-1 hover:text-foreground transition-colors cursor-pointer w-14 justify-end shrink-0 ml-2"
                    data-testid="donor-sort-count"
                  >
                    Count
                    {donorSort.field === 'count' ? (
                      donorSort.order === 'desc' ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />
                    ) : (
                      <ArrowUpDown className="w-3 h-3 opacity-30" />
                    )}
                  </button>
                  <button 
                    onClick={(e) => { e.stopPropagation(); handleDonorSort('total'); }}
                    className="flex items-center gap-1 hover:text-foreground transition-colors cursor-pointer w-20 justify-end shrink-0 ml-3"
                    data-testid="donor-sort-amount"
                  >
                    Amount
                    {donorSort.field === 'total' ? (
                      donorSort.order === 'desc' ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />
                    ) : (
                      <ArrowUpDown className="w-3 h-3 opacity-30" />
                    )}
                  </button>
                </div>
                {/* Donor Rows */}
                <div className="max-h-[400px] overflow-y-auto overflow-x-hidden pr-2">
                  {sortedDonors.slice(0, 20).map((donor, index) => {
                    const party = donor.party || 'unknown';
                    const bgClass = party === 'democrat' ? 'bg-blue-50/50 hover:bg-blue-100/50' : 
                                    party === 'republican' ? 'bg-red-50/50 hover:bg-red-100/50' : 
                                    'hover:bg-muted/50';
                    const textClass = party === 'democrat' ? 'text-blue-700' : 
                                      party === 'republican' ? 'text-red-700' : 
                                      'text-primary';
                    const amountClass = party === 'democrat' ? 'text-blue-600' : 
                                        party === 'republican' ? 'text-red-600' : 
                                        'text-emerald-600';
                    return (
                    <div 
                      key={donor.name} 
                      onClick={() => handleDonorClick(donor.name)}
                      className={`flex items-center py-2 border-b border-border/50 last:border-0 animate-slide-in cursor-pointer transition-colors rounded-sm px-2 -mx-2 ${bgClass}`}
                      style={{ animationDelay: `${index * 0.03}s` }}
                      data-testid={`donor-row-${index}`}
                    >
                      <span className={`font-mono text-xs w-6 shrink-0 ${party !== 'unknown' ? textClass : 'text-muted-foreground'}`}>
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <span className={`text-sm truncate hover:underline flex-1 min-w-0 ${textClass}`} title={donor.name}>
                        {donor.name}
                      </span>
                      <span className="text-xs text-muted-foreground w-14 text-right shrink-0 ml-2">
                        {donor.count.toLocaleString()}
                      </span>
                      <span className={`font-mono text-sm font-medium w-20 text-right shrink-0 ml-3 ${amountClass}`}>
                        {formatCurrency(donor.total)}
                      </span>
                    </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="py-8 text-center text-muted-foreground text-sm">
                No donor data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Recipients */}
        <Card className="border rounded-sm shadow-sm" data-testid="top-recipients-list">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg font-medium flex items-center gap-2 cursor-pointer hover:text-blue-600 transition-colors" onClick={() => navigate('/data')}>
              <Building2 className="w-4 h-4" />
              Top Recipients
              <ChevronRight className="w-4 h-4 ml-auto" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topRecipients.length > 0 ? (
              <div className="space-y-2">
                {/* Column Headers */}
                <div className="flex items-center py-2 border-b border-border text-xs uppercase tracking-wider text-muted-foreground font-medium pr-2">
                  <span className="w-6 shrink-0">#</span>
                  <span className="flex-1">Name</span>
                  <button 
                    onClick={(e) => { e.stopPropagation(); handleRecipientSort('count'); }}
                    className="flex items-center gap-1 hover:text-foreground transition-colors cursor-pointer w-14 justify-end shrink-0 ml-2"
                    data-testid="recipient-sort-count"
                  >
                    Count
                    {recipientSort.field === 'count' ? (
                      recipientSort.order === 'desc' ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />
                    ) : (
                      <ArrowUpDown className="w-3 h-3 opacity-30" />
                    )}
                  </button>
                  <button 
                    onClick={(e) => { e.stopPropagation(); handleRecipientSort('total'); }}
                    className="flex items-center gap-1 hover:text-foreground transition-colors cursor-pointer w-20 justify-end shrink-0 ml-3"
                    data-testid="recipient-sort-amount"
                  >
                    Amount
                    {recipientSort.field === 'total' ? (
                      recipientSort.order === 'desc' ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />
                    ) : (
                      <ArrowUpDown className="w-3 h-3 opacity-30" />
                    )}
                  </button>
                </div>
                {/* Recipient Rows */}
                <div className="max-h-[400px] overflow-y-auto overflow-x-hidden pr-2">
                  {sortedRecipients.slice(0, 20).map((recipient, index) => {
                    const party = recipient.party || getPartyColor(recipient.name);
                    const bgClass = party === 'democrat' ? 'bg-blue-50/50 hover:bg-blue-100/50' : 
                                    party === 'republican' ? 'bg-red-50/50 hover:bg-red-100/50' : 
                                    'hover:bg-muted/50';
                    const textClass = party === 'democrat' ? 'text-blue-700' : 
                                      party === 'republican' ? 'text-red-700' : 
                                      '';
                    const amountClass = party === 'democrat' ? 'text-blue-600' : 
                                        party === 'republican' ? 'text-red-600' : 
                                        'text-emerald-600';
                    
                    return (
                      <div 
                        key={recipient.name} 
                        onClick={() => handleRecipientClick(recipient.name)}
                        className={`flex items-center py-2 border-b border-border/50 last:border-0 animate-slide-in cursor-pointer transition-colors rounded-sm px-2 -mx-2 ${bgClass}`}
                        style={{ animationDelay: `${index * 0.03}s` }}
                        data-testid={`recipient-row-${index}`}
                      >
                        <span className={`font-mono text-xs w-6 shrink-0 ${textClass || 'text-muted-foreground'}`}>
                          {String(index + 1).padStart(2, '0')}
                        </span>
                        <span className={`text-sm truncate hover:underline font-medium flex-1 min-w-0 ${textClass || 'text-primary'}`} title={recipient.name}>
                          {recipient.name}
                        </span>
                        <span className="text-xs text-muted-foreground w-14 text-right shrink-0 ml-2">
                          {recipient.count.toLocaleString()}
                        </span>
                        <span className={`font-mono text-sm font-medium w-20 text-right shrink-0 ml-3 ${amountClass}`}>
                          {formatCurrency(recipient.total)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="py-8 text-center text-muted-foreground text-sm">
                No recipient data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
